'use client';
import React, { useState } from 'react';
import { Download, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import UtilizationReport from './UtilizationReport';
import OnTimeReport from './OnTimeReport';
import IdleTimeReport from './IdleTimeReport';
import JobCompletionReport from './JobCompletionReport';
import DriverActivityReport from './DriverActivityReport';

type ReportType = 'utilization' | 'ontime' | 'idle' | 'job-completion' | 'driver-activity';

const reportTypes: { key: ReportType; label: string; description: string }[] = [
  { key: 'utilization', label: 'Fleet Utilization', description: 'Asset deployment rates and idle time analysis' },
  { key: 'ontime', label: 'On-Time Performance', description: 'Arrival accuracy vs scheduled windows' },
  { key: 'idle', label: 'Idle Time Analysis', description: 'Engine-on but stationary time by vehicle' },
  { key: 'job-completion', label: 'Job Completion', description: 'Daily completion rates and delay breakdown' },
  { key: 'driver-activity', label: 'Driver Activity', description: 'Hours, mileage, and behavior per driver' },
];

const dateRanges = ['Today', 'Last 7 days', 'Last 30 days', 'This month', 'Last month', 'Custom'];

export default function ReportsView() {
  const [activeReport, setActiveReport] = useState<ReportType>('utilization');
  const [dateRange, setDateRange] = useState('Last 7 days');
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: 'csv' | 'pdf') => {
    setExporting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setExporting(false);
    toast.success(`Report exported as ${format.toUpperCase()}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">Reports</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Historical fleet performance analytics and compliance reports</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-medium text-zinc-300 rounded-lg transition-all disabled:opacity-60"
          >
            <Download size={13} /> Export CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs font-medium text-zinc-300 rounded-lg transition-all disabled:opacity-60"
          >
            <FileText size={13} /> Export PDF
          </button>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-zinc-500" />
          <span className="text-xs text-zinc-500 font-medium">Date Range:</span>
          <div className="flex items-center gap-1">
            {dateRanges.map((r) => (
              <button
                key={`range-${r}`}
                onClick={() => setDateRange(r)}
                className={`px-2.5 py-1 text-xs rounded-md font-medium transition-all ${dateRange === r ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'}`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-5">
        {/* Report type nav */}
        <div className="w-52 flex-shrink-0">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 space-y-0.5">
            {reportTypes.map((rt) => (
              <button
                key={`report-type-${rt.key}`}
                onClick={() => setActiveReport(rt.key)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all ${activeReport === rt.key ? 'bg-amber-500/15 border border-amber-500/30' : 'hover:bg-zinc-800'}`}
              >
                <p className={`text-xs font-semibold ${activeReport === rt.key ? 'text-amber-400' : 'text-zinc-300'}`}>{rt.label}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5 leading-snug">{rt.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Report content */}
        <div className="flex-1 min-w-0">
          {activeReport === 'utilization' && <UtilizationReport dateRange={dateRange} />}
          {activeReport === 'ontime' && <OnTimeReport dateRange={dateRange} />}
          {activeReport === 'idle' && <IdleTimeReport dateRange={dateRange} />}
          {activeReport === 'job-completion' && <JobCompletionReport dateRange={dateRange} />}
          {activeReport === 'driver-activity' && <DriverActivityReport dateRange={dateRange} />}
        </div>
      </div>
    </div>
  );
}